import React, { useState, useRef } from 'react';
import { Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle, Loader2, X, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { axiosInstance, API } from '../../App';
import secureStorage from '../../utils/secureStorage';
import { downloadFileWithProgress } from '../../lib/downloadProgress';
import { toast } from 'sonner';

const BulkRegistrationImport = ({ onImportComplete, onClose, autoOpen = false }) => {
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplateFallback = async () => {
    const token = secureStorage.getItem('token');
    const res = await fetch(`${API}/child-registration/import/template`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store'
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Template_Pendaftaran_Bulk.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await downloadFileWithProgress({
        label: 'Template Pendaftaran Bulk (Excel)',
        filename: 'Template_Pendaftaran_Bulk.xlsx',
        request: (onDownloadProgress) => axiosInstance.get('/child-registration/import/template', {
          responseType: 'blob',
          onDownloadProgress
        })
      });
      
      toast.success('Template berjaya dimuat turun!');
    } catch (error) {
      console.error('Download error, mencuba kaedah alternatif:', error);
      try {
        await downloadTemplateFallback();
        toast.success('Template berjaya dimuat turun!');
      } catch (fallbackError) {
        console.error('Fallback download error:', fallbackError);
        toast.error('Gagal memuat turun template');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Sila pilih fail Excel (.xlsx atau .xls)');
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Sila pilih fail untuk dimuat naik');
      return;
    }

    setIsUploading(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axiosInstance.post('/child-registration/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        if (onImportComplete) {
          onImportComplete();
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || 'Gagal mengimport data';
      toast.error(errorMessage);
      setImportResult({
        success: false,
        message: errorMessage,
        errors: []
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onClose) onClose();
  };

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg"
        data-testid="bulk-import-btn"
      >
        <FileSpreadsheet className="w-4 h-4 mr-2" />
        Import Bulk (Excel)
      </Button>

      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
              Import Pendaftaran Secara Bulk
            </DialogTitle>
            <DialogDescription>
              Muat naik fail Excel untuk mengimport atau mengemas kini pendaftaran kanak-kanak secara pukal.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Step 1: Download Template */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-blue-900 mb-1">Muat Turun Template</h3>
                  <p className="text-sm text-blue-700 mb-3">
                    Muat turun template Excel dan isikan maklumat pendaftaran mengikut format yang disediakan.
                  </p>
                  <Button
                    onClick={handleDownloadTemplate}
                    disabled={isDownloading}
                    variant="outline"
                    data-testid="download-template-btn"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    {isDownloading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Muat Turun Template
                  </Button>
                </div>
              </div>
            </div>

            {/* Step 2: Upload File */}
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-emerald-900 mb-1">Muat Naik Fail Excel</h3>
                  <p className="text-sm text-emerald-700 mb-3">
                    Pilih fail Excel yang telah diisi dengan maklumat pendaftaran.
                  </p>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="excel-upload"
                  />
                  
                  <div className="flex items-center gap-3">
                    <label
                      htmlFor="excel-upload"
                      className="cursor-pointer inline-flex items-center px-4 py-2 border border-emerald-300 rounded-lg text-emerald-700 hover:bg-emerald-100 transition-colors"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Pilih Fail
                    </label>
                    
                    {selectedFile && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-emerald-200">
                        <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-gray-700">{selectedFile.name}</span>
                        <button
                          onClick={() => {
                            setSelectedFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3: Import */}
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-amber-900 mb-1">Import Data</h3>
                  <p className="text-sm text-amber-700 mb-3">
                    Klik butang di bawah untuk memulakan proses import. Pendaftaran sedia ada akan dikemas kini.
                  </p>
                  <Button
                    onClick={handleUpload}
                    disabled={!selectedFile || isUploading}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sedang Import...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Import Sekarang
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Import Result */}
            {importResult && (
              <div className={`rounded-xl p-4 border ${
                importResult.success 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {importResult.success ? (
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <h3 className={`font-bold mb-2 ${
                      importResult.success ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {importResult.success ? 'Import Berjaya!' : 'Import Gagal'}
                    </h3>
                    <p className={`text-sm mb-2 ${
                      importResult.success ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {importResult.message}
                    </p>
                    
                    {importResult.success && (
                      <div className="flex gap-4 text-sm">
                        <span className="text-green-700">
                          <strong>{importResult.imported}</strong> baru
                        </span>
                        <span className="text-blue-700">
                          <strong>{importResult.updated}</strong> dikemas kini
                        </span>
                      </div>
                    )}
                    
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-red-200 max-h-32 overflow-y-auto">
                        <p className="text-xs font-bold text-red-800 mb-1">Ralat:</p>
                        {importResult.errors.map((error, idx) => (
                          <p key={idx} className="text-xs text-red-600">{error}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-600">
                  <p className="font-medium mb-1">Maklumat:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Template mengandungi 4 bahagian wajib: Persetujuan, Kanak-kanak, Ibu/Penjaga, Bapa/Penjaga</li>
                    <li>Medan bertanda * adalah wajib diisi</li>
                    <li>Pendaftaran sedia ada akan dikemas kini berdasarkan nama atau No. K/P kanak-kanak</li>
                    <li>Sila rujuk sheet "Arahan" dalam template untuk panduan lengkap</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkRegistrationImport;
